import { useState, type ReactNode } from "react";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStepActionContext {
  goPrev: () => void;
  goNext: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export interface WizardStep {
  id: string;
  label: string;
  render: () => ReactNode;
  /** Optional override for the action row. When omitted, default Back / Save draft / Next / Finish renders. */
  actions?: (ctx: WizardStepActionContext) => ReactNode;
}
export interface WizardProps {
  steps: readonly WizardStep[];
  onSaveDraft?: () => void;
  onFinish: () => void;
  /** Optional page-level title rendered as <h1>. Step label remains <h2>. */
  title?: string;
  /** Whether to render as a full page (default) or inside a dialog. */
  mode?: "page" | "dialog";
}

export function Wizard({ steps, onSaveDraft, onFinish, title, mode }: WizardProps) {
  const [index, setIndex] = useState(0);
  const step = steps[index]!;
  const ctx: WizardStepActionContext = {
    goPrev: () => setIndex((i) => Math.max(0, i - 1)),
    goNext: () => setIndex((i) => Math.min(steps.length - 1, i + 1)),
    isFirst: index === 0,
    isLast: index === steps.length - 1,
  };

  const content = (
    <>
      {title && <h1 className={cn("font-bold text-foreground", mode === "dialog" ? "mb-4 text-2xl" : "mb-4 text-3xl")}>{title}</h1>}
      <ol className="mb-6 flex w-full items-center text-sm">
        {steps.map((s, i) => (
          <li key={s.id} className={cn("flex items-center", i !== steps.length - 1 && "flex-1")}>
            <button
              type="button"
              disabled={i >= index}
              onClick={() => {
                if (i < index) setIndex(i);
              }}
              className={cn(
                "flex items-center gap-2 outline-none rounded-md ring-offset-background transition-colors",
                i < index && "hover:opacity-80 cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                i >= index && "cursor-default"
              )}
            >
              <div className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors",
                i === index ? "bg-primary text-primary-foreground" : 
                i < index ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
              )}>
                {i < index ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className={cn(
                "font-medium transition-colors hidden sm:block", 
                i === index ? "text-primary" : "text-muted-foreground"
              )}>
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div className="mx-2 h-px flex-1 bg-border sm:mx-4" />
            )}
          </li>
        ))}
      </ol>
      <h2 className={cn("font-semibold text-foreground", mode === "dialog" ? "mb-2 text-lg" : "mb-4 text-xl")}>{step.label}</h2>
      <Card className={cn("flex flex-col min-h-0", mode === "dialog" && "border-0 shadow-none bg-transparent flex-1")}>
        <CardContent className={cn("flex-1 overflow-y-auto", mode === "dialog" ? "px-1 pr-2 py-1" : "pt-6")}>{step.render()}</CardContent>
      </Card>
      <div className={cn("flex justify-between shrink-0", mode === "dialog" ? "mt-auto pt-6" : "mt-6")}>
        {step.actions ? (
          step.actions(ctx)
        ) : (
          <>
            <Button variant="outline" disabled={ctx.isFirst} onClick={ctx.goPrev}>Back</Button>
            <div className="flex gap-2">
              {onSaveDraft ? <Button variant="outline" onClick={onSaveDraft}>Save draft</Button> : null}
              {!ctx.isLast ? (
                <Button onClick={ctx.goNext}>Next</Button>
              ) : (
                <Button onClick={onFinish}>Finish</Button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  if (mode === "dialog") {
    return <div className="flex flex-col h-full">{content}</div>;
  }

  return <PageContainer>{content}</PageContainer>;
}
